import { useCallback, useRef } from 'react';

/**
 * This hook is used to "freeze" the current UI by rendering a DOM snapshot
 * on top of the page.
 *
 * Reason:
 * After payment success, we need to immediately clear multiple states
 * (cart, checkout data, user selections, etc.). Clearing these states
 * triggers React re-renders which can temporarily break the UI while
 * the page is still loading or redirecting.
 *
 * By freezing the UI, we decouple what the user sees from React's
 * rendering lifecycle, allowing state cleanup to happen safely
 * without visual flicker or layout breakage.
 *
 * This is especially important for critical flows like checkout/payment.
 */

export const useFreezeUI = () => {
    const overlayRef = useRef<HTMLDivElement | null>(null);

    const freeze = useCallback(() => {
        if (overlayRef.current) {
            return;
        }

        const scrollY = window.scrollY;
        const bodyClone = document.body.cloneNode(true);

        const overlay = document.createElement('div');
        overlay.style.position = 'fixed';
        overlay.style.inset = '0';
        overlay.style.zIndex = '9999';
        overlay.style.pointerEvents = 'none';
        overlay.style.background = 'white';
        overlay.style.overflow = 'auto';

        const inner = document.createElement('div');
        inner.style.minHeight = '100%';
        const contentHeight = document.documentElement.scrollHeight ?? document.body.scrollHeight;
        inner.style.height = `${Math.max(contentHeight, document.documentElement.clientHeight)}px`;
        inner.appendChild(bodyClone);

        overlay.appendChild(inner);
        document.body.appendChild(overlay);

        overlay.scrollTop = scrollY;

        overlayRef.current = overlay;
    }, []);

    const unfreeze = useCallback(() => {
        if (!overlayRef.current) {
            return;
        }
        overlayRef.current.remove();
        overlayRef.current = null;
    }, []);

    return {
        freeze,
        unfreeze,
        isFrozen: !!overlayRef.current,
    };
};
